import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Avatar } from './UIComponents';
import { getName } from '../../utils/studentUtils';
import { getIconByName } from '../../utils/iconMapping';
import { motion } from 'framer-motion';

/**
 * Generic ItemCard Component
 *
 * A flexible card component that consolidates StudentCard, SchoolCard, ValueCard patterns.
 * Supports multiple variants and types with consistent styling and behavior.
 *
 * @example
 * // Student card
 * <ItemCard type="student" data={student} variant="compact" onClick={handleClick} />
 *
 * // School card
 * <ItemCard type="school" data={school} variant="detailed" active={true} />
 *
 * // Value card
 * <ItemCard type="value" data={value} variant="list" clickable={true} />
 *
 * // Custom card
 * <ItemCard
 *   type="custom"
 *   data={item}
 *   renderIcon={() => <CustomIcon />}
 *   renderTitle={() => <CustomTitle />}
 * />
 */
const ItemCard = ({
  // Core props
  type = 'custom', // 'student', 'school', 'value', 'custom'
  data,

  // Variant & state
  variant = 'compact', // 'compact', 'result', 'detailed', 'list'
  active = false,

  // Interaction
  onClick,
  onHover,
  onRemove,
  clickable = false,
  enableNavigation = true,
  navigationPath,

  // Display options
  badge,
  subtitle,
  details,

  // Custom renders
  renderIcon,
  renderTitle,
  renderSubtitle,
  renderBadge,
  renderDetails,

  // Styling
  className = '',

  // Type-specific (student)
  mentionsMeta,

  // Type-specific (value)
  customColor
}) => {
  const navigate = useNavigate();

  if (!data) return null;

  // === HANDLERS ===

  const handleCardClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(data);
    }
  };

  const handleNavigationClick = (e) => {
    e.stopPropagation();
    if (enableNavigation && navigationPath) {
      navigate(navigationPath);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(data);
    }
  };

  // === STYLING ===

  const getCardClasses = () => {
    let baseClasses = 'rounded-xl transition-all duration-200 select-none ';

    // Add cursor if interactive
    if (onClick || clickable) {
      baseClasses += 'cursor-pointer ';
    }

    // Active state
    if (active) {
      baseClasses += 'ring-2 ring-primary bg-primary/5 ';
    } else {
      if (variant === 'list') {
        baseClasses += 'bg-base-100 ';
      } else {
        baseClasses += 'bg-base-100 hover:bg-base-200/50 ';
      }
    }

    // Shadow
    if (variant !== 'compact') {
      baseClasses += 'shadow-sm ';
    }

    // Padding by variant
    if (variant === 'compact') {
      baseClasses += 'p-2 ';
    } else if (variant === 'result' || variant === 'list') {
      baseClasses += 'p-3 ';
    } else {
      baseClasses += 'p-4 ';
    }

    return baseClasses + className;
  };

  const getIconSize = () => {
    return variant === 'compact' ? 'xs' : variant === 'result' ? 'sm' : 'md';
  };

  const getIconClasses = () => {
    return variant === 'compact' ? 'w-6 h-6' : variant === 'result' ? 'w-8 h-8' : 'w-10 h-10';
  };

  const getTextSize = (type) => {
    const sizes = {
      title: variant === 'compact' ? 'text-sm' : variant === 'result' ? 'text-base' : 'text-lg',
      subtitle: variant === 'compact' ? 'text-xs' : 'text-sm'
    };
    return sizes[type] || sizes.title;
  };

  // === TYPE-SPECIFIC RENDERING ===

  const renderIconByType = () => {
    if (renderIcon) {
      return renderIcon(data, variant);
    }

    switch (type) {
      case 'student': {
        const studentName = getName(data);
        const isClass = data.isClass;

        if (isClass) {
          return (
            <div
              className={`flex items-center justify-center rounded-full bg-accent text-accent-content font-bold ${
                variant === 'compact' ? 'w-6 h-6 text-xs' :
                variant === 'result' ? 'w-8 h-8 text-sm' :
                'w-10 h-10 text-base'
              }`}
              onClick={enableNavigation ? handleNavigationClick : undefined}
            >
              {data.photoURL || '🏫'}
            </div>
          );
        }

        return (
          <div onClick={enableNavigation ? handleNavigationClick : undefined}>
            <Avatar
              firstName={data.firstName || studentName.split(' ')[0]}
              surname={data.surname || data.lastName || studentName.split(' ').slice(1).join(' ')}
              photoURL={data.photoURL}
              size={getIconSize()}
            />
          </div>
        );
      }

      case 'school': {
        // getIconByName returns a React element, not a component
        const iconElement = data.icon ? getIconByName(data.icon, `${getIconClasses()} ${
          variant === 'compact' ? 'text-base-content/60' : active ? 'text-primary' : 'text-base-content/60'
        }`) : getIconByName('BuildingOfficeIcon', `${getIconClasses()} ${
          variant === 'compact' ? 'text-base-content/60' : active ? 'text-primary' : 'text-base-content/60'
        }`);

        return (
          <div
            onClick={enableNavigation ? handleNavigationClick : undefined}
            className={`flex items-center justify-center rounded-lg flex-shrink-0 ${
              variant === 'compact'
                ? ''
                : (active ? 'bg-primary/20' : 'bg-base-200')
            } ${variant === 'compact' ? 'p-1' : variant === 'result' ? 'p-2' : 'p-3'}`}
          >
            {iconElement}
          </div>
        );
      }

      case 'value': {
        if (data.icon) {
          return (
            <div className="flex-shrink-0">
              {getIconByName(data.icon, variant === 'compact' ? 'w-4 h-4' : 'w-5 h-5')}
            </div>
          );
        }
        return null;
      }

      default:
        return null;
    }
  };

  const getTitleText = () => {
    if (renderTitle) {
      return renderTitle(data, variant);
    }

    switch (type) {
      case 'student':
        return getName(data);
      case 'school':
        return data.name;
      case 'value':
        return data.name;
      default:
        return data.name || data.title || '';
    }
  };

  const getSubtitleText = () => {
    if (renderSubtitle) {
      return renderSubtitle(data, variant);
    }

    if (subtitle) {
      return subtitle;
    }

    switch (type) {
      case 'student':
        if (data.isClass && data.year) {
          return `Grade ${data.year}`;
        }
        return data.className || data.class;
      case 'school':
        return variant !== 'compact' ? data.slug : null;
      case 'value':
        return variant === 'list' ? data.description : null;
      default:
        return null;
    }
  };

  const getBadgeContent = () => {
    if (renderBadge) {
      return renderBadge(data, variant, active);
    }

    if (badge) {
      return badge;
    }

    switch (type) {
      case 'student':
        if (!data.isClass && mentionsMeta?.count > 0) {
          return (
            <div className={`badge badge-error ${variant === 'compact' ? 'badge-xs' : 'badge-sm'}`}>
              {mentionsMeta.count}
            </div>
          );
        }
        return null;

      case 'school':
        if (active) {
          if (variant === 'compact') {
            return <CheckIcon className="w-5 h-5 text-primary flex-shrink-0" />;
          }
          return (
            <div className={`badge badge-primary ${variant === 'compact' ? 'badge-xs' : 'badge-sm'}`}>
              Active
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  const getDetailsContent = () => {
    if (renderDetails) {
      return renderDetails(data, variant);
    }

    if (details) {
      return details;
    }

    if (variant !== 'detailed') {
      return null;
    }

    switch (type) {
      case 'student':
        return (
          <div className="flex items-center justify-between text-sm text-base-content/60">
            <span>Mentions today: {mentionsMeta?.count || 0}</span>
            {data.lastMentionDate && (
              <span>Last: {new Date(data.lastMentionDate).toLocaleDateString()}</span>
            )}
          </div>
        );

      case 'school':
        if (data.createdAt) {
          return (
            <div className="flex items-center justify-between text-sm text-base-content/60">
              <span>Created: {new Date(data.createdAt.toDate()).toLocaleDateString()}</span>
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  // === CUSTOM COLOR SUPPORT (for Value cards) ===

  const hasCustomColor = type === 'value' && customColor && customColor !== '#3B82F6';
  const colorStyle = hasCustomColor ? {
    backgroundColor: `${customColor}20`,
    borderColor: `${customColor}40`,
    borderWidth: '1px',
    color: customColor
  } : {};

  // === MOTION SUPPORT ===

  const Component = (clickable || onClick) && type === 'value' ? motion.div : 'div';
  const motionProps = (clickable || onClick) && type === 'value' ? {
    whileHover: { scale: 1.02 },
    whileTap: { scale: 0.98 }
  } : {};

  // === RENDER ===

  const titleText = getTitleText();
  const subtitleText = getSubtitleText();
  const badgeContent = getBadgeContent();
  const detailsContent = getDetailsContent();

  return (
    <Component
      className={getCardClasses()}
      style={hasCustomColor ? colorStyle : undefined}
      onClick={onClick ? handleCardClick : undefined}
      onMouseEnter={() => onHover && onHover(data)}
      {...motionProps}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* Icon/Avatar */}
        {renderIconByType()}

        {/* Title & Subtitle */}
        <div className="flex-1 min-w-0">
          <div
            className={`font-medium text-base-content truncate ${getTextSize('title')}`}
            onClick={enableNavigation ? handleNavigationClick : undefined}
          >
            {titleText}
          </div>

          {subtitleText && (
            <div className={`text-base-content/60 truncate ${getTextSize('subtitle')}`}>
              {subtitleText}
            </div>
          )}
        </div>

        {/* Badge */}
        {badgeContent}

        {/* Remove Button */}
        {onRemove && (
          <button
            onClick={handleRemove}
            className="btn btn-ghost btn-circle btn-xs ml-1 opacity-60 hover:opacity-100"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Details Section (for detailed variant) */}
      {detailsContent && (
        <div className="mt-2 pt-2 border-t border-base-300">
          {detailsContent}
        </div>
      )}
    </Component>
  );
};

export { ItemCard };
export default ItemCard;
